// Internal modules
import { appStore } from '@/store';
import { $logger } from '@/lib/logger';
import { fallback } from '@/lib/utils';
import { getContent } from '@/lib/content';
import { focusOnFirstError } from '@/lib/utils';
import { getAccessToken } from '@/lib/globals';
import { ADDRESS_VALIDATION_ERROR } from './constants';
import { FIELD_LEVEL_ERRORS } from './mappings';

const fallbackToHermesOrXoon = ({ message }) => {
  const isSignUpToken = getAccessToken().type === 'signup';

  fallback({
    app: isSignUpToken ? 'hermes' : 'xoon',
    reason: message,
  });
};

const handle3DSError = ({ message }) => {
  // Custom error we throw for 3DS failures
  fallback({
    additionalParams: {
      redirectToHermesOn3DSDecline: 'weasley',
    },
    app: 'hermes',
    reason: message,
  });
};

const handleDefaultError = ({ errorData, message = '' } = {}) => {
  const error = message.toLowerCase().replace(/\s/g, '_');
  const errorDataCode = errorData?.[0]?.code;

  $logger.error(`unhandled_contingency_${error}`, {
    error,
    errorDataCode,
  });

  // TODO: once we've mapped all the errors and ramped we should remove this
  // or change it to a generic error page so we don't have to fallback
  fallbackToHermesOrXoon({ message: 'unhandledContingency' });
};

// Some errors are fallbacks for signup but are page level errors for guest
const handlePostAccountCreationError = ({ contentKey, error, message }) => {
  const { form: formState } = appStore.getState();
  const { value: onboardOption } = formState.onboardOption;
  const isGuest = onboardOption === 'guest';
  if (isGuest) {
    return handlePageLevelError({ contentKey, error });
  }

  fallbackToHermesOrXoon({ message });
};

const handleAddressValidationError = ({ data = [] }) => {
  const { form: formState } = appStore.getState();
  const { form: formActions } = appStore.getActions();

  /**
   * If the user has selected to ship to their billing address then
   * we can just iterate over the billing address fields.
   *
   * - `data` looks like: [
   *    {
   *      field: 'city',
   *      code: 'INVALID_CITY',
   *      argumentsPath: ['billingAddress', 'city']
   *    },
   *    ...
   *  ]
   * - Using a Set to get the unique values since there will be an object in
   *  `data` for each of the three fields associated with the validation error
   *  and therefore we would end up with each address type being included in
   *  the array three times. e.g. ['shippingAddress', 'shippingAddress', etc]
   */
  const addressTypes = formState.shipToBilling.value
    ? ['billingAddress']
    : new Set(data.map(obj => obj.argumentsPath[0]));
  const { CLIENT_ERROR_CODE, FIELDS } = ADDRESS_VALIDATION_ERROR;

  // Update the validation error for the necessary address types
  addressTypes.forEach(addressType => {
    formActions.updateAddressValidationError({
      action: 'setError',
      addressType,
      errorCode: CLIENT_ERROR_CODE,
      fields: FIELDS,
    });
  });
};

const handlePageLevelError = ({ contentKey, error }) => {
  const { checkpoints, message } = error;
  const { ui: uiState } = appStore.getState();

  if (uiState.pageLevelError === contentKey) {
    return $logger.info('existing_page_level_error', {
      error: message,
    });
  }

  /**
   * If the error is an address validation error we highlight
   * the three address fields that the error pertains to.
   *
   * Checking the checkpoints here as well as the error code since
   * the error code that is returned in pretty generic, VALIDATION_ERROR,
   * and could potentially be returned for other errors as well as the
   * address validation error.
   */
  if (
    message === ADDRESS_VALIDATION_ERROR.API_ERROR_CODE &&
    checkpoints?.includes(ADDRESS_VALIDATION_ERROR.API_CHECKPOINT)
  ) {
    handleAddressValidationError(error);
  }

  $logger.info('setting_page_level_error', { error: message });

  const uiActions = appStore.getActions().ui;
  uiActions.setPageLevelError(contentKey);
  uiActions.toggleIsPageLoading(false);

  window.scrollTo(0, 0);
};

const handleNamesValidationError = error => {
  if (error?.errorData?.length) {
    const actions = appStore.getActions();
    const filteredErrors = error.errorData.filter(f => f);
    filteredErrors.forEach(({ code }) => {
      const { contentKey, field } = FIELD_LEVEL_ERRORS[code];
      const payload = {
        displayError: true,
        error: getContent(contentKey),
        errorCode: code,
        isValid: false,
      };

      actions.form.updateNonAddressField({
        field,
        payload,
      });
    });
    return focusOnFirstError(appStore.getState().form);
  }
};

const isNameValidationError = error => {
  if (error?.errorData?.length) {
    const namesFields = /user.first_name|user.last_name/;
    const filteredErrors = error.errorData.filter(f => f);
    const [firstField = {}] = filteredErrors;
    return namesFields.test(firstField.fields);
  }
};

export {
  fallbackToHermesOrXoon,
  handle3DSError,
  handleDefaultError,
  handleNamesValidationError,
  handlePageLevelError,
  handlePostAccountCreationError,
  isNameValidationError,
};
